import { DEMO_ADMIN, DEMO_COLLEAGUE, DEMO_MARKETING } from './seed/admin'
import { DEMO_PEOPLE, demoAdminIdentity, type Identity } from './adminIdentity'
import { deniedContentParts } from '../core/rules/contentAccess'
import { demoHandoffBackend } from './handoffBackend'
import { localCatalog } from '../data/catalog'
import { localSite } from '../data/site'
import { pages } from '../data/pages'
import { brandKit, designs } from '../admin/marketing/marketingSeed'
import { newsletters } from '../admin/newsletter/newsletterSeed'
import { socialIdeas, socialPosts } from '../admin/social/socialSeed'
import { customerName, summarize } from '../admin/customers/customerRules'
import { applySubscriptionAction } from '../core/rules/subscription'
import { assertRefillAction } from './refillGuard'
import { policy } from '../data/policies'
import { ApiError } from '../core/api/http'
import { today } from '../core/format/dates'
import { db, toInboxOrder, type AppointmentRow } from './db'
import { demoId, reply } from './reply'
import { assertFree, demoAgendaBackend, durationOf, finishAppointment, nextSession } from './agendaBackend'
import { DEFAULT_STAFF_ID } from '../core/rules/agenda'
import { nextStatus as FULFILMENT_NEXT } from '../admin/inbox/inboxRules'
import type { Account, Appointment, Subscription } from '../core/types/account'
import type { SubscriptionAction } from '../core/types/requests'
import type { AdminContent, AdminUser, InboxBooking } from '../core/types/adminRequests'
import type { AdminBackend, DemoHint, DemoLogin } from '../core/types/backend'
import { demoTwoFactor } from './twoFactor'
import { demoSalesBackend, fulfilmentEvents } from './salesBackend'
import { demoVoucherBackend } from './voucherBackend'
import { demoMailBackendFor, lockedNewsletterChanged, stripDeliveries, withDeliveries } from './mailBackend'
import { resolveKuurOrders } from './kuurOrders'
import { demoKassaBackend } from './kassaBackend'
import { demoStaffBackend } from './staffBackend'
import { demoAudit, demoTeam } from './teamStore'
import { ENDPOINT_PERMISSIONS, logClass, mayCall } from '../core/rules/endpointPermissions'
import { bookStock, demoStockBackend } from './stockBackend'
import { demoStockReceiptsCountsBackend } from './stockReceiptsCountsBackend'
import { demoInsightsBackend } from './insightsBackend'
import { demoCostsBackend } from './costsBackend'
import { customerCreateProblem, emailEditable } from '../core/rules/customerMatch'
import { repointHandoffNotes } from './handoffBackend'
import { mergeAccounts, mergeNotes, mergeProblem } from '../core/rules/customerMerge'
import { newDemoAccount } from './seed/factory'
import { withException } from '../core/rules/absence'
import type { SalonDayException } from '../core/types/site'

/**
 * DEMO — het beheer aan de kant van de backend: klanten, inbox en het
 * beheer-concept. Leest en schrijft dezelfde tabellen als de webshop en
 * Mijn omgeving (demo/db.ts).
 */

/**
 * `required: false`: in de demo mag Sarah 2FA overslaan, zodat ze het beheer aan anderen kan
 * laten zien. De echte server zet dit op `true` (docs/LARAVEL.md §13.1b).
 */
const adminUser = (): AdminUser => {
  const me = demoAdminIdentity.current()
  const roles = demoAdminIdentity.roles()
  const access = demoAdminIdentity.access()
  return {
    email: demoAdminIdentity.person().email,
    name: me.name,
    // Oudere velden, voor een beheer dat de rechten nog niet leest.
    role: roles.includes('eigenaar') ? 'owner' : 'staff',
    staffId: me.staffId,
    accountId: me.accountId,
    personId: me.staffId,
    roles,
    permissions: access.grants,
    limits: access.limits,
    twoFactor: { enabled: demoTwoFactor.enabled(), required: false },
  }
}

/** Idempotentie van "Nieuwe klant" (per tabblad; de server bewaart de requestId bij de klant). */
const createdByRequest = new Map<string, string>()
/** Idempotentie van abonnementsacties in het beheer (per tabblad, zoals de server per `requestId`). */
const subscriptionRequests = new Map<string, { action: string; subscription: Subscription | null }>()

/** Een rij in de klantenlijst: de samenvatting plus wat alleen het beheer weet. */
const listRow = (a: Account) => ({ ...summarize(a), bookingBlocked: Boolean(db.bookingBlock(a.customer.id)) })

/** Voor het inlogscherm van het beheer (via adminApi.demoAdminLogin). */
export const demoAdminLogin: DemoLogin = {
  email: DEMO_ADMIN.email,
  password: DEMO_ADMIN.password,
  label: DEMO_ADMIN.name,
  description: 'Testaccount voor het beheer',
}

/** Tweede testaccount: de collega (via adminApi.demoColleagueLogin). */
export const demoColleagueLogin: DemoLogin = {
  email: DEMO_COLLEAGUE.email,
  password: DEMO_COLLEAGUE.password,
  label: DEMO_COLLEAGUE.name,
  description: 'Testaccount van de collega: alleen haar eigen agenda en kassa',
}

/** Derde testaccount: marketing (via adminApi.demoMarketingLogin). */
export const demoMarketingLogin: DemoLogin = {
  email: DEMO_MARKETING.email,
  password: DEMO_MARKETING.password,
  label: DEMO_MARKETING.name,
  description: 'Testaccount voor marketing: teksten, social, nieuwsbrief en Inzicht',
}

/** Voor de tweede inlogstap in de demo: de code die de app nu zou tonen (via adminApi.demoTwoFactorHint). */
export async function demoTwoFactorHint(): Promise<DemoHint | null> {
  const code = await demoTwoFactor.currentCode()
  return code ? { label: 'Code die je app nu toont', value: code } : null
}

const wrongCode = async (): Promise<never> => {
  await reply(null, 400)
  throw new ApiError('Die code klopt niet. Kijk of de tijd op je telefoon goed staat, en probeer de nieuwste code.', 422)
}

/** De voorraad zoals de "producttabel" hem kent, over het concept heen gelegd. */
function withStock(content: AdminContent): AdminContent {
  const overrides = db.stockOverrides()
  return {
    ...content,
    catalog: {
      ...content.catalog,
      products: content.catalog.products.map((p) => (p.id in overrides ? { ...p, stock: overrides[p.id] ?? undefined } : p)),
    },
  }
}

/**
 * Het opgeslagen concept hoort bij een bepaalde stand van de lokale data. Pas je de
 * bestanden in data/ aan, dan wint die nieuwe stand (anders zie je je wijziging niet).
 */
function fingerprint(): string {
  const text = JSON.stringify([localCatalog, localSite(), pages, designs, brandKit, socialPosts, socialIdeas])
  let hash = 5381
  for (let i = 0; i < text.length; i++) hash = (hash * 33) ^ text.charCodeAt(i)
  return `${text.length}-${(hash >>> 0).toString(36)}`
}

/** Wijzigt één klant, of meldt dat die niet (meer) bestaat. */
function change<T>(id: string, fn: (a: Account) => [Account, T]): T {
  if (!db.account(id)) throw new ApiError('Deze klant bestaat niet (meer).', 404)
  return db.changeAccount(id, fn) as T
}

function withAppointment(a: Account, aid: string, patch: (x: Appointment) => Appointment): [Account, Appointment] {
  const current = a.appointments.find((x) => x.id === aid)
  if (!current) throw new ApiError('Deze afspraak bestaat niet meer.', 404)
  const updated = patch(current)
  return [{ ...a, appointments: a.appointments.map((x) => (x.id === aid ? updated : x)) }, updated]
}

/** Een afspraakrij in de vorm van de inbox, voor een klant of een gast. */
function toInboxBooking(row: AppointmentRow): InboxBooking | null {
  const account = row.customerId ? db.account(row.customerId) : null
  const contact = account
    ? { name: customerName(account.customer), email: account.customer.email, firstVisit: account.visits.length === 0 }
    : row.guest
  if (!contact) return null
  return {
    id: row.id,
    receivedAt: row.receivedAt,
    customerId: row.customerId,
    status: row.status,
    change: row.request ?? 'nieuw',
    booking: { treatmentSlug: row.treatmentSlug, date: row.date, time: row.time, ...contact },
    ...(row.handledBy ? { handledBy: row.handledBy } : {}),
  }
}

/** Een open afspraak van deze klant, of 404 (ook als hij al afgerond of geannuleerd is). */
function openRowOf(customerId: string, aid: string): AppointmentRow {
  const row = db.appointments().find((a) => a.id === aid && a.customerId === customerId && !a.outcome)
  if (!row) throw new ApiError('Deze afspraak bestaat niet meer.', 404)
  return row
}

/** Wat er staat: opgeslagen, of (nooit opgeslagen) de lokale data als versie 0. */
const storedContent = (): AdminContent =>
  db.content(fingerprint()) ?? {
    revision: 0,
    catalog: localCatalog,
    site: localSite(),
    pages,
    marketing: { designs, brandKit, posts: socialPosts, ideas: socialIdeas, newsletters },
  }

/** Waar het logboek over gaat: de id uit het adres of het antwoord, met een leesbare naam. */
function auditTarget(args: unknown[], result: unknown): { id: string; label: string } {
  const out = (result && typeof result === 'object' ? result : {}) as Record<string, unknown>
  const id = typeof args[0] === 'string' ? args[0] : typeof out.id === 'string' ? out.id : ''
  const account = id ? db.account(id) : undefined
  if (account) return { id, label: customerName(account.customer) }
  const number = typeof out.number === 'string' ? out.number : ''
  const who = typeof out.customerName === 'string' ? out.customerName : ''
  return { id, label: [number, who].filter(Boolean).join(' · ') || id || '—' }
}

/**
 * Zoals de server, per aanroep (core/rules/endpointPermissions.ts): sessie nog geldig (niet
 * ingetrokken, verlopen of overal uitgelogd → 401), bevoegdheid (403 `forbidden`), recente
 * bevestiging bij team en export (423 `reauth_required`), en na succes een regel in het logboek.
 * Eigenaarschap per rij (haar afspraak, haar bon, haar klant) controleert elke functie daarna zelf.
 */
/**
 * Zonder sessie bereikbaar: inloggen, de tweede stap, uitloggen en "ben ik ingelogd?". Al het andere
 * (ook de inhoud, 2FA instellen en opnieuw bevestigen) vraagt een sessie.
 */
const PUBLIC_CALLS = new Set<keyof AdminBackend>(['fetchAdminSession', 'adminLogin', 'adminLogout', 'twoFactorChallenge'])

function permissionGate(backend: AdminBackend): AdminBackend {
  const gated = {} as Record<string, unknown>
  for (const [key, fn] of Object.entries(backend) as [keyof AdminBackend, unknown][]) {
    const rule = ENDPOINT_PERMISSIONS[key]
    if (typeof fn !== 'function' || PUBLIC_CALLS.has(key)) {
      gated[key] = fn
      continue
    }
    gated[key] = async (...args: unknown[]) => {
      // Zonder sessie (uitgelogd, nieuw tabblad, of nog midden in de tweede inlogstap): 401, net als
      // de server. Vroeger liep de aanroep dan gewoon door, als Sarah.
      if (!db.adminSession()) {
        await reply(null, 150)
        throw new ApiError('Je bent niet (meer) ingelogd.', 401)
      }
      const me = demoAdminIdentity.current()
      const account = demoTeam.byStaff(me.staffId)
      if (!demoTeam.usable(account) || !demoTeam.sessionValid(account)) {
        db.setAdminSession(false)
        await reply(null, 150)
        throw new ApiError('Je bent niet (meer) ingelogd.', 401)
      }
      if (!('session' in rule)) {
        if (!mayCall(demoAdminIdentity.access(), key)) {
          await reply(null, 200)
          throw new ApiError('Dit deel is alleen voor Sarah.', 403, undefined, 'forbidden')
        }
        if (rule.reauth && !demoTeam.reauthFresh()) {
          await reply(null, 150)
          throw new ApiError('Bevestig eerst even dat jij het bent.', 423, undefined, 'reauth_required')
        }
      }
      const result = await (fn as (...a: unknown[]) => Promise<unknown>).apply(backend, args)
      const logged = 'log' in rule ? rule.log : undefined
      if (logged && db.adminSession()) {
        const me = demoAdminIdentity.current()
        demoAudit.add({ accountId: me.accountId, name: me.name, action: logged.action, target: { kind: logged.kind, ...auditTarget(args, result) }, cls: logClass(rule) })
      }
      return result
    }
  }
  return gated as unknown as AdminBackend
}

/** Zonder marketing-bevoegdheid geen ontwerpen, posts of nieuwsbrieven in wording: die horen niet bij dat werk. */
function forViewer(content: AdminContent): AdminContent {
  if (demoAdminIdentity.can('marketing.social')) return content
  return { ...content, marketing: { ...content.marketing, designs: [], posts: [], ideas: [], newsletters: [] } }
}

/**
 * Zoals de server: de afwijkende dag van "afwezig melden" komt ook in het opgeslagen beheerblok,
 * met een nieuwe `revision`. Anders was hij na verversen weg (het beheer laadt de site uit dat blok).
 * Staat hij er al (herhaling van dezelfde aanvraag), dan geen nieuwe versie.
 */
function storeAbsenceException(exception: SalonDayException): number {
  // Nog nooit opgeslagen: de lokale data (die de gedeelde lijst al bijwerkte) wordt versie 1.
  const saved = db.content(fingerprint())
  const stored = saved ?? storedContent()
  const before = stored.site.salonExceptions ?? []
  const salonExceptions = withException(before, exception)
  if (saved && JSON.stringify(salonExceptions) === JSON.stringify(before)) return stored.revision
  const next = { ...stored, revision: stored.revision + 1, site: { ...stored.site, salonExceptions } }
  db.setContent(fingerprint(), next)
  return next.revision
}

const calls: AdminBackend = {
  ...demoAgendaBackend,
  async reportAbsence(req) {
    const result = await demoAgendaBackend.reportAbsence(req)
    return { ...result, revision: storeAbsenceException(result.exception) }
  },
  ...demoHandoffBackend,
  fetchAdminSession: async () => (db.adminSession() ? adminUser() : null),

  async adminLogin(req) {
    const email = req.email.trim().toLowerCase()
    const who = (Object.keys(DEMO_PEOPLE) as Identity[]).find((id) => DEMO_PEOPLE[id].email === email && DEMO_PEOPLE[id].password === req.password)
    const owner = who === 'eigenaar'
    // Een ander alleen zolang de login bestaat en niet verlopen is (Team en toegang).
    if (!who || (!owner && !demoTeam.usable(demoTeam.byStaff(DEMO_PEOPLE[who].staffId)))) {
      await reply(null, 450)
      throw new ApiError('Dat e-mailadres of wachtwoord klopt niet.', 401)
    }
    demoAdminIdentity.set(who)
    // Sarah kan haar eigen login niet kwijt (laatste eigenaar); in de demo staat hij er altijd.
    if (owner && !demoTeam.byStaff('sarah')) {
      demoTeam.put({ accountId: 'acc-sarah', staffId: 'sarah', email: DEMO_ADMIN.email, status: 'actief', roles: ['eigenaar'], validUntil: null, epoch: 0, sessions: [] })
    }
    demoTeam.signedIn(demoAdminIdentity.current().staffId)
    // Met 2FA aan: nog geen sessie, eerst de code.
    if (demoTwoFactor.enabled()) {
      demoTwoFactor.setPending(true)
      return reply({ status: 'two_factor' as const }, 450)
    }
    db.setAdminSession(true)
    return reply({ status: 'ok' as const, user: adminUser() }, 450)
  },

  async adminLogout() {
    demoTwoFactor.setPending(false)
    demoTeam.signedOut(demoAdminIdentity.current().staffId)
    db.setAdminSession(false)
  },

  async twoFactorChallenge(req) {
    if (!demoTwoFactor.pending()) throw new ApiError('Log eerst opnieuw in met je wachtwoord.', 401)
    if (!(await demoTwoFactor.check(req))) return wrongCode()
    demoTwoFactor.setPending(false)
    db.setAdminSession(true)
    return reply(adminUser(), 350)
  },

  async twoFactorSetup() {
    if (!db.adminSession()) throw new ApiError('Je bent niet (meer) ingelogd.', 401)
    return reply(await demoTwoFactor.setup(demoAdminIdentity.person().email), 300)
  },

  async twoFactorConfirm(req) {
    if (!db.adminSession()) throw new ApiError('Je bent niet (meer) ingelogd.', 401)
    const recoveryCodes = await demoTwoFactor.confirm(req.code)
    if (!recoveryCodes) return wrongCode()
    return reply({ user: adminUser(), recoveryCodes }, 350)
  },

  async regenerateRecoveryCodes() {
    if (!db.adminSession()) throw new ApiError('Je bent niet (meer) ingelogd.', 401)
    return reply({ recoveryCodes: demoTwoFactor.regenerate() }, 300)
  },

  fetchAdminContent() {
    // Met de voorraad uit de producttabel en de verzendstand van de nieuwsbrieven (zoals de server
    // die altijd meestuurt).
    return reply(forViewer(withDeliveries(withStock(storedContent()))), 250)
  },

  // Zoals de server: versie controleren, en voorraad van bestaande producten niet uit het blok halen.
  async saveAdminContent(content) {
    const stored = db.content(fingerprint())
    const current = stored?.revision ?? 0
    if (content.revision !== current) {
      await reply(null, 300)
      throw new ApiError('Het beheer is intussen ergens anders opgeslagen.', 409, undefined, 'version_conflict')
    }
    // Per gewijzigd deel de bevoegdheid daarvan (LARAVEL.md §11.3), zoals de server.
    const denied = deniedContentParts(storedContent(), stripDeliveries(content), demoAdminIdentity.access())
    if (denied.length > 0) {
      await reply(null, 300)
      throw new ApiError(
        `Je mag ${denied.join(', ').toLowerCase()} niet aanpassen. Verwerp die wijzigingen en sla opnieuw op.`,
        403,
        { content: denied.join(', ') },
        'forbidden',
      )
    }
    if (lockedNewsletterChanged(stored, content)) {
      await reply(null, 300)
      throw new ApiError('Een ingeplande of verstuurde nieuwsbrief kan niet meer veranderen. Annuleer eerst het inplannen.', 409, undefined, 'newsletter_locked')
    }
    // De echte voorraad = de producttabel (overrides), anders wat er opgeslagen stond.
    const overrides = db.stockOverrides()
    const known = new Map(
      (stored?.catalog ?? localCatalog).products.map((p) => [p.id, p.id in overrides ? (overrides[p.id] ?? undefined) : p.stock]),
    )
    const next = {
      ...stripDeliveries(content),
      revision: current + 1,
      catalog: {
        ...content.catalog,
        products: content.catalog.products.map((p) => (known.has(p.id) ? { ...p, stock: known.get(p.id) } : p)),
      },
    }
    db.setContent(fingerprint(), next)
    return reply(withDeliveries(withStock(next)), 350)
  },

  async updateStock(productId, { stock, expected }) {
    const products = (db.content(fingerprint())?.catalog ?? localCatalog).products
    const product = products.find((p) => p.id === productId)
    if (!product) {
      await reply(null, 250)
      throw new ApiError('Dit product bestaat niet (meer). Sla eerst je nieuwe product op.', 404)
    }
    const overrides = db.stockOverrides()
    const current = productId in overrides ? overrides[productId] : (product.stock ?? null)
    if (current !== expected) return reply({ stock: current, applied: false }, 300)
    // Een getal zetten is een telling op de webshopplek: in het log met het verschil. "Niet bijhouden" heeft geen mutatie.
    if (stock === null) db.setStock(productId, null)
    else if (stock !== current) bookStock(productId, { reason: 'telling', location: 'webshop', qty: current === null ? stock : stock - current, note: 'Via de productpagina' })
    return reply({ stock, applied: true }, 300)
  },

  fetchCustomers: () => reply(db.accounts().map(listRow), 250),
  // De demo vraagt geen recente login; de server wel (423 `reauth_required`).
  exportCustomers: () => reply(db.accounts().map(listRow), 400),
  fetchCustomer(id) {
    const account = db.account(id)
    // "Laatst ingezien door …": de vorige keer uit het logboek (deze keer komt er pas na dit antwoord bij).
    return reply(account ? { account, internalNote: db.note(id), bookingBlock: db.bookingBlock(id), lastViewed: demoAudit.lastViewed(id) } : null, 250)
  },

  async createCustomer(req) {
    const known = createdByRequest.get(req.requestId)
    if (known) return reply({ id: known }, 200)
    const problem = customerCreateProblem(req)
    if (problem) {
      await reply(null, 250)
      throw new ApiError(problem.message, 422, { [problem.field]: problem.message })
    }
    const email = req.email.trim()
    const existing = email ? db.accountByEmail(email) : null
    if (existing) {
      await reply(null, 250)
      throw new ApiError(`Dit e-mailadres hoort al bij ${customerName(existing.customer)}.`, 409, { email: 'Bestaat al', duplicateId: existing.customer.id }, 'duplicate')
    }
    // Zonder login: met een e-mailadres kan ze later zelf een inloglink vragen (dan pas bevestigd).
    const account = newDemoAccount({ firstName: req.firstName, lastName: req.lastName, email, phone: req.phone, hasPassword: false, emailVerified: false })
    db.putAccount(account)
    createdByRequest.set(req.requestId, account.customer.id)
    return reply({ id: account.customer.id }, 350)
  },

  async saveCustomerContact(id, req) {
    const current = db.account(id)
    if (!current) {
      await reply(null, 250)
      throw new ApiError('Deze klant bestaat niet (meer).', 404)
    }
    const problem = customerCreateProblem(req)
    if (problem) {
      await reply(null, 250)
      throw new ApiError(problem.message, 422, { [problem.field]: problem.message })
    }
    const before = current.customer
    const email = req.email.trim().toLowerCase()
    const emailChanged = email !== before.email.trim().toLowerCase()
    if (emailChanged && !emailEditable(before)) {
      await reply(null, 250)
      throw new ApiError(`${before.firstName} logt in met dit adres; dat wijzigt ze zelf in Mijn omgeving.`, 409, { email: 'Vast' }, 'email_locked')
    }
    const owner = emailChanged && email ? db.accountByEmail(email) : null
    if (owner && owner.customer.id !== id) {
      await reply(null, 250)
      throw new ApiError(`Dit e-mailadres hoort al bij ${customerName(owner.customer)}. Voeg de dossiers samen.`, 409, { email: 'Bestaat al', duplicateId: owner.customer.id }, 'duplicate')
    }
    const saved = change(id, (a) => {
      const customer = {
        ...a.customer,
        firstName: req.firstName.trim(),
        lastName: req.lastName.trim(),
        phone: req.phone.trim(),
        email,
        // Een nieuw adres is nog niet bevestigd; de server stuurt de bevestigingsmail niet zelf (geen login).
        ...(emailChanged ? { emailVerified: false } : {}),
      }
      return [{ ...a, customer }, customer]
    })
    return reply(saved, 350)
  },

  async mergeCustomers(id, { sourceId }) {
    const target = db.account(id)
    const source = db.account(sourceId)
    if (!target || !source) {
      await reply(null, 250)
      throw new ApiError('Een van de twee dossiers bestaat niet (meer).', 404)
    }
    const problem = mergeProblem(target, source)
    if (problem) {
      await reply(null, 300)
      throw new ApiError(problem.message, 409, undefined, problem.code)
    }
    const merged = mergeAccounts(target, source)
    db.mergeCustomer(id, sourceId, merged, mergeNotes(db.note(id), db.note(sourceId), customerName(source.customer)))
    repointHandoffNotes(sourceId, id, customerName(merged.customer))
    return reply({ account: db.account(id) ?? merged, internalNote: db.note(id), bookingBlock: db.bookingBlock(id), lastViewed: demoAudit.lastViewed(id) }, 500)
  },

  savePlan: (id, plan) => reply(change(id, (a) => [{ ...a, plan }, plan]), 400),
  saveCourse(id, course) {
    const saved = change(id, (a) => [{ ...a, course }, course])
    // Een vooruitbetaalde kuur vastgelegd: de melding "kuur koppelen" in de inbox is dan afgehandeld.
    resolveKuurOrders(id, course)
    return reply(saved, 300)
  },

  saveNote(id, req) {
    if (!db.account(id)) throw new ApiError('Deze klant bestaat niet (meer).', 404)
    db.setNote(id, req.internalNote)
    return reply(req, 300)
  },

  saveBookingBlock(id, { blocked, reason }) {
    if (!db.account(id)) throw new ApiError('Deze klant bestaat niet (meer).', 404)
    if (!blocked) {
      db.setBookingBlock(id, null)
      return reply({ bookingBlock: null }, 300)
    }
    const why = reason.trim()
    if (!why) throw new ApiError('Schrijf kort op waarom.', 422, { reason: 'Schrijf kort op waarom.' })
    const bookingBlock = { reason: why.slice(0, 300), since: today(), ...demoAdminIdentity.actor() }
    db.setBookingBlock(id, bookingBlock)
    return reply({ bookingBlock }, 300)
  },

  addAppointment(id, { requestId: _requestId, session: _suggested, ...rest }) {
    assertFree({ date: rest.date, time: rest.time, staffId: DEFAULT_STAFF_ID, durationMinutes: durationOf(rest.treatmentSlug) })
    // Het sessienummer rekent de server zelf, net als in de agenda; niet dat van het dossier.
    const session = nextSession(id, rest.treatmentSlug)
    const appointment: Appointment = { id: demoId('af').toLowerCase(), status: 'bevestigd', ...rest, ...(session ? { session } : {}) }
    return reply(change(id, (a) => [{ ...a, appointments: [...a.appointments, appointment] }, appointment]), 400)
  },

  confirmAppointment: (id, aid) =>
    reply(
      change(id, (a) => withAppointment(a, aid, (x) => ({ ...x, status: 'bevestigd', request: undefined }))),
      350,
    ),

  proposeAppointment: (id, aid, req) =>
    reply(
      change(id, (a) =>
        withAppointment(a, aid, (x) => {
          // Zelfde regel als overal: een bezette tijd geeft 409 `slot_taken`.
          assertFree({ date: req.date, time: req.time, staffId: DEFAULT_STAFF_ID, durationMinutes: durationOf(x.treatmentSlug) }, aid)
          return { ...x, ...req, status: 'bevestigd', request: undefined }
        }),
      ),
      400,
    ),

  /**
   * Afspraak afronden: wordt een bezoek, en telt mee in de kuur als het een kuursessie was.
   * Dezelfde afhandeling als "Afronden" in de agenda; de afspraak blijft daar als afgerond staan.
   */
  completeAppointment(id, aid) {
    if (!db.account(id)) throw new ApiError('Deze klant bestaat niet (meer).', 404)
    const row = openRowOf(id, aid)
    const result = finishAppointment(row, { used: [], advice: [] }, '')
    if (!result) throw new ApiError('Deze klant bestaat niet (meer).', 404)
    return reply(result, 400)
  },

  /** Annuleren door de salon: blijft in de agenda zichtbaar als geannuleerd. */
  async removeAppointment(id, aid) {
    openRowOf(id, aid)
    db.changeAppointment(aid, (r) => ({ ...r, outcome: 'geannuleerd', cancelledBy: 'salon' }))
    await reply(null, 350)
  },

  customerSubscriptionAction(id, req) {
    // Zelfde id nog eens = hetzelfde antwoord (verloren antwoord); de actie gebeurt één keer.
    const key = `${id}:${req.requestId}`
    const action: SubscriptionAction = req
    const fingerprint = JSON.stringify({ ...req, requestId: undefined })
    const seen = subscriptionRequests.get(key)
    if (seen) {
      if (seen.action !== fingerprint) throw new ApiError('Deze aanvraag hoort bij een andere wijziging. Probeer het opnieuw.', 422, undefined, 'request_mismatch')
      return reply(seen.subscription, 200)
    }
    const subscription = change(id, (a) => {
      // Zelfde bewaking als bij de klant: geen te kleine doos, niet later dan iets op is.
      assertRefillAction(a.subscription, action)
      const next = applySubscriptionAction(a.subscription, action, policy.subscription)
      return [{ ...a, subscription: next }, next]
    })
    subscriptionRequests.set(key, { action: fingerprint, subscription })
    return reply(subscription, 350)
  },

  markDiaryEntry: (id, entryId, req) =>
    reply(
      change(id, (a) => {
        if (req.marketing && !a.customer.diaryConsent)
          throw new ApiError(`${a.customer.firstName} heeft geen toestemming gegeven voor gebruik van haar foto’s.`, 403)
        const entry = a.diary.find((d) => d.id === entryId)
        if (!entry) throw new ApiError('Deze foto bestaat niet meer.', 404)
        const updated = { ...entry, marketing: req.marketing }
        return [{ ...a, diary: a.diary.map((d) => (d.id === entryId ? updated : d)) }, updated]
      }),
      300,
    ),

  fetchInbox: () =>
    reply(
      {
        orders: db
          .orders()
          .filter((o) => o.fulfilment !== 'bezorgd' && !o.cancelledAt)
          .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
          .map(toInboxOrder),
        // Aanvragen van klanten, en alle afspraken van gasten (ook bevestigd, anders verdwijnen ze uit beeld).
        bookings: db
          .appointments()
          // Plus wat een collega afhandelde, zodat Sarah ziet "door Mila bevestigd" (zolang hij open is).
          .filter((a) => a.status === 'aanvraag' || !a.customerId || (a.handledBy && a.handledBy.staffId !== DEFAULT_STAFF_ID && !a.outcome))
          .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
          .map(toInboxBooking)
          .filter((b): b is InboxBooking => b !== null),
        leads: db.leads(),
        newsletter: db.newsletter(),
      },
      250,
    ),

  /** Eén rij: Mijn omgeving leest dezelfde status ("Verzonden" = "Onderweg"). */
  setOrderStatus(orderId, status, tracking) {
    // Zoals de server (LARAVEL.md §9.5): alleen betaalde orders, alleen één stap vooruit. Dezelfde
    // stand nog eens (dubbele klik, tweede tabblad) is geen fout. Een verouderd tabblad kan een
    // verzonden order zo niet terugzetten naar "ingepakt".
    const current = db.orders().find((o) => o.id === orderId)
    if (!current) throw new ApiError('Deze bestelling bestaat niet meer.', 404)
    if (current.payment.status !== 'betaald') throw new ApiError('Deze bestelling is nog niet betaald.', 422)
    if (current.cancelledAt) throw new ApiError('Deze bestelling is geannuleerd.', 409)
    // Al in deze stand: niets wijzigen (geen tweede verzendmail, tracking niet overschrijven).
    if (current.fulfilment === status) return reply(toInboxOrder(current), 200)
    // 'bezorgd' is de eindstand: daarna kan het beheer niets meer vooruitzetten.
    const next = current.fulfilment === 'bezorgd' ? undefined : FULFILMENT_NEXT[current.fulfilment]
    if (next !== status) {
      throw new ApiError('Deze bestelling is intussen al verder verwerkt. De inbox is ververst.', 409)
    }
    const updated = db.changeOrder(orderId, (o) => ({
      ...o,
      fulfilment: status,
      ...(tracking ? { tracking } : {}),
      // Voor de tijdlijn in Bestellingen.
      events: [...(o.events ?? []), ...fulfilmentEvents(status)],
    }))
    if (!updated) throw new ApiError('Deze bestelling bestaat niet meer.', 404)
    return reply(toInboxOrder(updated), 350)
  },

  confirmGuestBooking(id) {
    const updated = db.changeAppointment(id, (a) => ({ ...a, status: 'bevestigd', request: undefined }))
    const booking = updated && toInboxBooking(updated)
    if (!booking) throw new ApiError('Deze aanvraag bestaat niet meer.', 404)
    return reply(booking, 350)
  },

  ...demoSalesBackend,
  ...demoVoucherBackend,
  ...demoKassaBackend,
  ...demoStaffBackend,
  ...demoStockBackend,
  ...demoStockReceiptsCountsBackend,
  ...demoInsightsBackend,
  ...demoCostsBackend,
  ...demoMailBackendFor(() => db.content(fingerprint())),
}

export const demoAdminBackend: AdminBackend = permissionGate(calls)
